import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { PaginaNode } from '@gql/graphql'
import useFindMenu from '@hooks/useFindMenu'
import useTrans from '@hooks/useTrans'
import { BRAND } from '@utils/settings'
import { handleHeaderMenuTracking, handleHeaderTracking } from '@utils/tracking'
import {
  HeaderMenuItem,
  LangDropdown,
  Logo,
  SearchTrigger,
  WContainer,
  WImage,
  WLink,
} from '@components/atoms'
import { Nav, SearchModal } from '@components/organisms'
import styles from './Header.module.sass'
import { MenuItemType } from '@utils/types'

interface Props {
  pagina?: PaginaNode
  className?: string
}

const Header = (props: Props) => {
  const { pagina = undefined, className = '' } = props

  const t = useTrans()
  const router = useRouter()

  const utilsMenu = useFindMenu('utils')
  const topMenu = useFindMenu('top')
  const mainMenu = useFindMenu('main')
  const logoEgoMenu = useFindMenu('logo-ego')

  const isHome = pagina?.chiave == 'homepage'

  const [openMenuItem, setOpenMenuItem] = useState(undefined)
  const [menuMobileOpen, setMenuMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  return (
    <>
      <header
        className={`${styles.root} ${searchOpen ? styles['root--searchOpen'] : ''} ${className}`}
      >
        <div className={styles.top}>
          <WContainer>
            <div className={styles.topLayout}>
              <div className={styles.utils}>
                {['it-it', 'int'].includes(router.locale) && <LangDropdown />}
                {!!utilsMenu?.children?.length && (
                  <ul className={styles.utilsMenu}>
                    {utilsMenu.children.map((x) => (
                      <li key={x.pk}>
                        <HeaderMenuItem
                          item={x}
                          onClick={() => handleHeaderTracking(x.name)}
                          size="sm"
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {!!topMenu?.children?.length && (
                <ul className={styles.topMenu}>
                  {topMenu.children
                    .filter((tm: MenuItemType) => tm.key !== 'logo-ego')
                    .map((x) => (
                      <>
                        <li key={x.pk}>
                          <HeaderMenuItem
                            item={x}
                            onClick={() => {
                              handleHeaderTracking(x.name)
                            }}
                            size="sm"
                          />
                        </li>
                        <li className={styles.verticalDivider} />
                      </>
                    ))}
                  {logoEgoMenu && logoEgoMenu.image && (
                    <WLink
                      className={styles.logoEgoBoxImage}
                      href={logoEgoMenu.url}
                      onClick={() => {
                        handleHeaderTracking('logo-ego')
                      }}
                      newPage
                    >
                      <WImage maxWidth={62.5} maxHeight={24} src={logoEgoMenu.image} />
                    </WLink>
                  )}
                </ul>
              )}
            </div>
          </WContainer>
        </div>
        <div className={styles.main}>
          <WContainer>
            <div className={styles.mainLayout}>
              <div className={styles.mainList}>
                <WLink
                  href={'/'}
                  onClick={() => handleHeaderTracking('logo')}
                  className={styles.logo}
                >
                  {isHome ? (
                    <h1 aria-label={BRAND}>
                      <Logo />
                    </h1>
                  ) : (
                    <Logo />
                  )}
                </WLink>
                <HeaderMenuItem
                  item={{ icon: 'menu', name: t('Menu') }}
                  variant="icon"
                  onClick={() => {
                    handleHeaderTracking('burgermenu')
                    setMenuMobileOpen(!menuMobileOpen)
                  }}
                  className={styles.menuTriggerMobile}
                />
                {!!mainMenu?.children?.length && (
                  <nav className={styles.nav}>
                    <ul>
                      {mainMenu.children.map((x) => (
                        <li key={x.pk}>
                          <ul>
                            {x.children?.map((y) => (
                              <li key={y.pk}>
                                <HeaderMenuItem
                                  item={y}
                                  onClick={() => {
                                    handleHeaderMenuTracking(y.name, 0)
                                    if (!y.children?.length) {
                                      setOpenMenuItem(undefined)
                                    } else {
                                      setOpenMenuItem(openMenuItem === y ? undefined : y)
                                    }
                                  }}
                                />
                                {!!y.children?.length && (
                                  <Nav
                                    level={1}
                                    item={y}
                                    open={openMenuItem === y}
                                    setOpen={(v) => {
                                      if (v) {
                                        setOpenMenuItem(y)
                                      } else {
                                        setOpenMenuItem(undefined)
                                      }
                                    }}
                                  />
                                )}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}
              </div>

              <div className={styles.mainActions}>
                <SearchTrigger
                  onClick={() => {
                    handleHeaderTracking('search_bar')
                    setSearchOpen(true)
                  }}
                  className={styles.searchDesktop}
                />
              </div>
            </div>
          </WContainer>
        </div>
        <div className={styles.searchMobileBox}>
          <WContainer>
            {!searchOpen && (
              <SearchTrigger
                onClick={() => {
                  handleHeaderTracking('search_bar')
                  setSearchOpen(true)
                }}
                className={styles.searchMobile}
              />
            )}
          </WContainer>
        </div>
        <Nav
          item={{
            ...mainMenu,
            children: (mainMenu?.children || []).concat([
              {
                pk: 'extra_mobile',
                style: 'categorie',
                children: [
                  ...(topMenu?.children || []),
                  ...(utilsMenu?.children || []),
                  logoEgoMenu,
                ],
              },
            ]),
          }}
          level={0}
          open={menuMobileOpen}
          setOpen={setMenuMobileOpen}
        />
      </header>
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  )
}

export default Header
